import { clsx } from 'clsx';
import { Children, type FC, type JSX, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';

import { addPx, getItemTestId } from './internal/utils';

import styles from './WrapElementsSpacer.css';

type Space = 0 | 2 | 4 | 8 | 12 | 16;

type WrapElementsSpacerProps = {
  children: Array<ReactNode> | ReactNode;
  verticalSpace: Space;
  horizontalSpace: Space;
  containerTestId?: string;
  itemTestId?: string | ((index: number) => string);
  /** @default true */
  wrapItems?: boolean;
  /** @default center */
  alignItems?: 'center' | 'bottom' | 'top';
  direction?: 'column' | 'row';
  onClick?(event: ReactMouseEvent<HTMLDivElement>): void;
};

export const WrapElementsSpacer: FC<WrapElementsSpacerProps> = props => {
  const {
    children,
    verticalSpace,
    horizontalSpace,
    wrapItems = true,
    alignItems = 'center',
    onClick,
    containerTestId,
    itemTestId,
    direction = 'row',
  } = props;

  if (Children.count(children) === 0) {
    return null;
  }

  const realVerticalSpace = verticalSpace / 2;
  const realHorizontalSpace = horizontalSpace / 2;

  const childStyle = { padding: `${addPx(realVerticalSpace)} ${addPx(realHorizontalSpace)}` };
  const containerStyle = { margin: `${addPx(-realVerticalSpace)} ${addPx(-realHorizontalSpace)}` };

  const childrenArrWithWrapper = Array.of<JSX.Element>();
  Children.forEach(children, (child, index) => {
    if (child || typeof child === 'number') {
      childrenArrWithWrapper.push(
        <div className={styles['item']} data-testid={getItemTestId(index, itemTestId)} key={index} style={childStyle}>
          {child}
        </div>,
      );
    }
  });

  if (!childrenArrWithWrapper.length) {
    return null;
  }

  return (
    <div
      className={clsx(
        styles['container'],
        wrapItems ? styles['wrap'] : styles['nowrap'],
        styles[`align-${alignItems}`],
        direction && styles[`direction-${direction}`],
      )}
      data-testid={containerTestId}
      style={containerStyle}
      onClick={onClick}
    >
      {childrenArrWithWrapper}
    </div>
  );
};
